import Honeybadger from "@honeybadger-io/js"

import { raisedByBrowserExtension } from "./helpers/browser_extension_error"
import { turboNetworkFailure } from "./helpers/turbo_network_error"

// Get Honeybadger API key from meta tag
const apiKeyMeta = document.querySelector('meta[name="honeybadger-api-key"]')
const apiKey = apiKeyMeta ? apiKeyMeta.getAttribute("content") : null

// Get user information from meta tags
const getUserInfo = () => {
  const userId = document.querySelector('meta[name="honeybadger-user-id"]')?.getAttribute("content")
  const userName = document
    .querySelector('meta[name="honeybadger-user-name"]')
    ?.getAttribute("content")
  const userUsername = document
    .querySelector('meta[name="honeybadger-user-username"]')
    ?.getAttribute("content")
  const userEmail = document
    .querySelector('meta[name="honeybadger-user-email"]')
    ?.getAttribute("content")
  const userRole = document
    .querySelector('meta[name="honeybadger-user-role"]')
    ?.getAttribute("content")
  const userTenant = document
    .querySelector('meta[name="honeybadger-user-tenant"]')
    ?.getAttribute("content")

  if (userId) {
    const userInfo = {
      id: userId,
      username: userUsername,
      name: userName,
      role: userRole,
      tenant: userTenant,
    }

    // Only add email if it exists (employees have email, customers don't)
    if (userEmail) {
      userInfo.email = userEmail
    }
    return userInfo
  }
  return null
}

// Initialize Honeybadger only if API key is found
if (apiKey) {
  Honeybadger.configure({
    apiKey: apiKey,
    environment: window.location.hostname.includes("localhost") ? "development" : "production",
    enableUnhandledRejection: true,
    // `endpoint`, NOT `apiUrl`. @honeybadger-io/js has no `apiUrl` option and reads
    // it nowhere, so it was silently ignored and every notice went to the default
    // https://api.honeybadger.io — the US region. BauTask's account is EU, the US
    // endpoint rejects an EU key with 403, and every browser error from every
    // tenant was being dropped with nothing on either side to say so.
    endpoint: "https://eu-api.honeybadger.io",
  })

  // Configure ignored errors (same list as previous Sentry config)
  Honeybadger.beforeNotify((notice) => {
    const ignoredMessages = [
      // Mobile Safari aborts fetches when navigating away or backgrounding the page.
      // Turbo's StreamObserver doesn't catch this, causing unhandled rejections.
      // This is benign browser behavior, not an application error.
      "AbortError",
      "Fetch is aborted",
      "The operation was aborted",
      "The user aborted a request",
      // Mobile Safari uses this message for failed fetch requests (e.g., user backgrounded the app)
      "Load failed",
      // Firefox throws TypeError with this message when a fetch is in-flight during navigation.
      // Chrome/Safari throw AbortError (caught above); this is the Firefox equivalent.
      "NetworkError when attempting to fetch resource",
      // iOS WKWebView messageHandlers conflict with console instrumentation
      // while a third-party map SDK initializes. Harmless — no user impact.
      "undefined is not an object (evaluating 'window.webkit.messageHandlers",
      // Chromium reports a ResizeObserver whose callback resized an observed element within
      // the same frame; the notifications are simply delivered next frame. It carries no
      // stack (`/settings:0:0`), and our observers only schedule work (setTimeout) rather
      // than resizing synchronously — the usual source is an extension's own observer.
      // Older Chromium worded it "loop limit exceeded".
      "ResizeObserver loop completed with undelivered notifications",
      "ResizeObserver loop limit exceeded",
    ]

    const message = notice.message || ""
    if (ignoredMessages.some((ignored) => message.includes(ignored))) {
      return false
    }

    // A Turbo navigation or frame load that lost its connection (Chrome's wording).
    // Turbo already recovers; only its rethrown promise goes unhandled.
    if (turboNetworkFailure(message, notice.backtrace)) {
      return false
    }

    // Errors raised entirely inside scripts the visitor's own browser injected
    // into the page (extensions, userscripts) are not ours to fix.
    if (raisedByBrowserExtension(notice.backtrace)) {
      return false
    }
  })

  // Set user context if user is logged in
  const userInfo = getUserInfo()
  if (userInfo) {
    Honeybadger.setContext({
      user_id: userInfo.id,
      user_email: userInfo.email,
      user_username: userInfo.username,
      user_name: userInfo.name,
      user_role: userInfo.role,
      tenant: userInfo.tenant,
    })
  }
}

export default Honeybadger
